import { Alert, Badge, Card, CardContent, CardHeader, CardTitle } from '@shared/components';
import { formatIsoDateLong } from '@/app/utils/formatDate';
import { formatMoneyCurrency } from '@/shared/utils/formatMoneyDisplay';
import { DetailList, type DetailField } from '../../DetailList';
import { isPositiveAmount, REASON_LABEL, REFUND_STATUS_LABEL, REQUESTING_PARTY_LABEL } from '../../../utils/cancellationCopy';
import type { CancellationPanelData } from '../../../types/cancellation.types';

interface Props {
    cancellation: CancellationPanelData;
}

const headingId = 'agreement-cancellation-heading';

const NOT_FINAL = 'Worked out when the cancellation completes';

function feeValue(cancellation: CancellationPanelData): string {
    if (!cancellation.figuresFinal) return NOT_FINAL;
    return cancellation.feeWaived ? 'Waived (cooling-off period)' : formatMoneyCurrency(cancellation.feeCharged);
}

function RefundValue({ cancellation }: Props) {
    if (cancellation.noCustomerRefund) return <>No refund on this campaign</>;
    if (!cancellation.figuresFinal) return <>{NOT_FINAL}</>;
    const { refundOwed, refundStatus } = cancellation;
    if (refundOwed === null || refundStatus === null) return <>{isPositiveAmount(refundOwed) ? NOT_FINAL : 'No refund due'}</>;
    return (
        <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{formatMoneyCurrency(refundOwed)}</span>
            <Badge variant={refundStatus === 'failed' ? 'error' : 'warning'}>{REFUND_STATUS_LABEL[refundStatus]}</Badge>
        </span>
    );
}

function detailFields(cancellation: CancellationPanelData): DetailField[] {
    return [
        { label: 'Reason', value: REASON_LABEL[cancellation.reason] },
        { label: 'Asked by', value: REQUESTING_PARTY_LABEL[cancellation.requestingParty] },
        { label: 'Case reference', value: cancellation.caseReference },
        { label: 'Started by', value: cancellation.cancelledBy },
        { label: 'Started on', value: formatIsoDateLong(cancellation.requestedAt) },
        {
            label: 'Cancelled on',
            value: cancellation.cancelledAt ? formatIsoDateLong(cancellation.cancelledAt) : 'Not yet cancelled',
        },
        { label: 'Cancellation fee', value: feeValue(cancellation) },
        { label: 'Refund', value: <RefundValue cancellation={cancellation} /> },
    ];
}

export function CancellationPanel({ cancellation }: Props) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <span id={headingId}>Cancellation</span>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <section aria-labelledby={headingId} className="space-y-4">
                    <DetailList fields={detailFields(cancellation)} emptyMessage="No cancellation details recorded." />
                    {cancellation.note && (
                        <div>
                            <h3 className="mb-1 text-sm font-semibold text-mp-foreground">Note</h3>
                            <p className="whitespace-pre-wrap text-sm text-mp-foreground">{cancellation.note}</p>
                        </div>
                    )}
                    {cancellation.closedInArtemis && (
                        <Alert variant="info" role="note" className="items-start">
                            <div className="space-y-1">
                                <p className="font-semibold">
                                    Closed in Artemis{cancellation.completedBy ? ` by ${cancellation.completedBy}` : ''}
                                </p>
                                <p>MotorPlan Payments was not contacted.</p>
                                {cancellation.completionNote && (
                                    <p className="whitespace-pre-wrap">{cancellation.completionNote}</p>
                                )}
                            </div>
                        </Alert>
                    )}
                </section>
            </CardContent>
        </Card>
    );
}
